import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Copy, FileText, Plus, PencilSimple, Trash, Star, UploadSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import { toHtml, sanitize, htmlToPlain, isLayoutPreservedHtml } from "@/lib/richtext";

// Static per-category config: label / heading / URL param / whether the
// import should preserve the original PDF layout exactly.
const CATEGORY_CONFIG = {
  resolution: {
    label: "Resolutions", singular: "Resolution",
    empty: "No resolution templates yet. Paste an existing resolution PDF and let AI extract it.",
    preserveLayout: false,
  },
  form: {
    label: "Forms", singular: "Form",
    empty: "No form templates yet. Import a PDF and its layout will be preserved exactly.",
    preserveLayout: true,
  },
  letter: {
    label: "Letters", singular: "Letter",
    empty: "No letter templates yet. Import a PDF and its layout will be preserved exactly.",
    preserveLayout: true,
  },
};

const TYPE_LABEL = { director: "Directors'", member: "Members'" };
const TYPE_KIND  = { director: "Directors' Resolution", member: "Members' Resolution" };

const emptyForm = { title: "", body: "", type: "director", is_default: false };

export default function ResolutionTemplates() {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user && user.role === "admin";
  const isLibrary = location.pathname.startsWith("/library");
  const [libraryTab, setLibraryTab] = useState("form"); // used only when isLibrary
  const category = isLibrary
    ? libraryTab
    : location.pathname.startsWith("/forms") ? "form"
    : location.pathname.startsWith("/letters") ? "letter"
    : "resolution";
  const cfg = CATEGORY_CONFIG[category];

  const [templates, setTemplates] = useState([]);
  const [active, setActive] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const extractRef = useRef(null);

  const load = () => api.get(`/resolution-templates?category=${category}`).then(r => {
    setTemplates(r.data);
    if (active) {
      const still = r.data.find(t => t.key === active.key);
      setActive(still || r.data[0] || null);
    } else if (r.data.length) {
      setActive(r.data[0]);
    } else { setActive(null); }
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); setActive(null); }, [category]);

  const copyBody = () => {
    if (!active) return;
    navigator.clipboard.writeText(htmlToPlain(active.body)).then(() => toast.success("Template copied"));
  };

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({ title: t.title, body: t.body, type: t.type || "director", is_default: !!t.is_default });
    setOpen(true);
  };

  // Layout-preserved imports (Forms / Letters from PDF) cannot go through
  // TipTap without losing the SVG positioning. Editing is limited to title.
  const isLayoutOnly = (t) => isLayoutPreservedHtml(t?.body);

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    // For SVG-based layout-preserved bodies we keep the imported body as-is;
    // for normal bodies require non-empty text content.
    if (!isLayoutPreservedHtml(form.body) && !htmlToPlain(form.body).trim()) {
      return toast.error("Body required");
    }
    setSaving(true);
    try {
      const payload = { ...form, category };
      if (editing) await api.put(`/custom-templates/${editing.id}`, payload);
      else await api.post("/custom-templates", payload);
      toast.success(editing ? `${cfg.singular} updated` : `${cfg.singular} saved`);
      setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    await api.delete(`/custom-templates/${t.id}`);
    if (active?.key === t.key) setActive(null);
    load();
  };

  const toggleDefault = async (t) => {
    try {
      const { data } = await api.put(`/custom-templates/${t.id}/toggle-default`);
      toast.success(data.is_default ? `"${t.title}" is now shared with all staff` : `"${t.title}" is now private`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const shareAllMine = async () => {
    if (!window.confirm("Share ALL your templates (Resolutions, Forms and Letters) with every staff account?")) return;
    try {
      const { data } = await api.post("/custom-templates/share-all");
      toast.success(data.shared > 0 ? `Shared ${data.shared} template(s) with all staff` : "All your templates are already shared");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const dedupe = async () => {
    if (!window.confirm(
      "Delete duplicate templates?\n\n" +
      "For each title with more than one copy, the MOST RECENTLY UPDATED version is kept and the rest are permanently deleted. This is what usually fixes 'my edit isn't showing' — an older row was being displayed instead of your edit."
    )) return;
    try {
      const { data } = await api.post("/custom-templates/dedupe");
      if (data.deleted > 0) {
        toast.success(`Removed ${data.deleted} duplicate template(s) across ${data.groups_touched} title(s)`);
      } else {
        toast.success("No duplicates found — every template title is unique");
      }
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };


  // Duplicate detection for the currently loaded list — flag any title that
  // appears more than once. The frontend list is sorted so the admin sees
  // the flag next to each affected row.
  const titleCounts = templates.reduce((acc, t) => {
    const k = (t.title || "").toLowerCase().trim();
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
  const hasDuplicates = Object.values(titleCounts).some(n => n > 1);

  const extractFromPdf = async (file) => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (cfg.preserveLayout) fd.append("preserve_layout", "true");
      const token = localStorage.getItem("cosec_token");
      const res = await fetch(`${API}/custom-templates/extract-from-pdf`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text() || "Extraction failed");
      const data = await res.json();
      if (cfg.preserveLayout) {
        // Auto-save the exact-layout import (page-as-image). The image is
        // pixel-identical to the source PDF and doesn't need review —
        // users add placeholder annotations later via the edit dialog.
        // Admin uploads are auto-shared with all staff so the firm's library
        // stays in one place (staff can still create their own private ones).
        await api.post("/custom-templates", {
          title: data.title,
          body: data.body,
          type: data.type || "director",
          category,
          is_default: !!isAdmin,
        });
        toast.success(`Imported "${data.title}"${isAdmin ? " — shared as default for all staff" : ""}`);
        await load();
      } else {
        setEditing(null);
        setForm({ title: data.title, body: data.body, type: data.type });
        setOpen(true);
        toast.success("Extracted — review and save");
      }
    } catch (e) { toast.error(String(e.message || e).slice(0, 240)); }
    finally {
      setExtracting(false);
      if (extractRef.current) extractRef.current.value = "";
    }
  };

  const customCount = templates.filter(t => t.is_custom).length;

  return (
    <div className="p-10 max-w-[1400px]">
      <header className="mb-8 flex items-end justify-between gap-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Library</div>
          <h1 className="mt-2 text-4xl font-semibold tracking-tight">
            {isLibrary                        && "Forms & Letters"}
            {!isLibrary && category === "resolution" && "Resolution Master Templates"}
            {!isLibrary && category === "form"       && "Form Master Templates"}
            {!isLibrary && category === "letter"     && "Letter Master Templates"}
          </h1>
          {isLibrary && (
            <div className="mt-3 inline-flex border border-border rounded-sm overflow-hidden bg-white" data-testid="library-tabs">
              <button type="button"
                      className={`px-4 py-1.5 text-sm ${libraryTab === "form" ? "bg-[#0F62FE] text-white" : "hover:bg-muted"}`}
                      onClick={() => setLibraryTab("form")}
                      data-testid="library-tab-form">Forms</button>
              <button type="button"
                      className={`px-4 py-1.5 text-sm border-l border-border ${libraryTab === "letter" ? "bg-[#0F62FE] text-white" : "hover:bg-muted"}`}
                      onClick={() => setLibraryTab("letter")}
                      data-testid="library-tab-letter">Letters</button>
            </div>
          )}
          <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
            {category === "resolution"
              ? "Standard Directors' Circular Resolution templates plus your firm's own custom ones. Auto-fills company name, SSM and meeting date when used from a client's Resolutions tab."
              : "Import a PDF and its original layout is preserved exactly — no reflow, no reformatting. Placeholders like {{COMPANY_NAME}}, {{SSM}}, {{TODAY}} still auto-fill when used on a client."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && hasDuplicates && (
            <Button variant="outline" className="rounded-sm h-10 border-[#DA1E28] text-[#DA1E28] hover:bg-[#DA1E28]/5"
                    onClick={dedupe}
                    data-testid="dedupe-templates-btn"
                    title="Duplicate titles were detected — keep the most recently edited copy and delete the rest">
              Clean up duplicates
            </Button>
          )}
          {isAdmin && (
            <>
              <Button variant="outline" className="rounded-sm h-10"
                      onClick={shareAllMine}
                      data-testid="share-all-templates-btn"
                      title="Mark every template you own as Default so staff can use them">
                <Star size={16} weight="fill" className="mr-2 text-[#0F62FE]" />
                Share all with staff
              </Button>
            </>
          )}
          <input ref={extractRef} type="file" accept="application/pdf" hidden
                 onChange={e => extractFromPdf(e.target.files?.[0])} data-testid="extract-template-input" />
          <Button variant="outline" className="rounded-sm h-10" onClick={() => extractRef.current?.click()}
                  disabled={extracting} data-testid="extract-template-btn">
            <UploadSimple size={16} weight="bold" className="mr-2" />
            {extracting ? "Importing…" : cfg.preserveLayout ? "Import PDF (exact layout)" : "Extract from PDF"}
          </Button>
          <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE] h-10" onClick={openNew}
                  data-testid="add-template-btn">
            <Plus size={16} weight="bold" className="mr-2" /> New {cfg.singular}
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6">
        <aside className="border border-border" data-testid="template-list">
          <header className="px-4 py-2 border-b border-border font-mono text-[10px] uppercase tracking-widest text-muted-foreground flex items-center justify-between">
            <span>{templates.length} {templates.length === 1 ? cfg.singular.toLowerCase() : cfg.label.toLowerCase()}</span>
            <span>{customCount} custom</span>
          </header>
          {templates.length === 0 && (
            <div className="p-6 text-xs text-muted-foreground">{cfg.empty}</div>
          )}
          {templates.map(t => {
            const canEdit = isAdmin || !t.is_default || t.owner_user_id === user?.id;
            const badge = t.is_default ? "Default" : "Custom";
            return (
            <div key={t.key}
                 className={`group border-b last:border-b-0 border-border flex items-stretch ${active?.key === t.key ? "bg-muted/60" : "hover:bg-muted/50"}`}>
              <button onClick={() => setActive(t)}
                      data-testid={`template-item-${t.key}`}
                      className="flex-1 min-w-0 text-left px-4 py-3">
                <div className="flex items-start gap-2">
                  {isAdmin ? (
                    <button type="button"
                            onClick={(e) => { e.stopPropagation(); toggleDefault(t); }}
                            className="mt-0.5 shrink-0 hover:scale-125 transition-transform"
                            title={t.is_default ? "Shared with all staff — click to make private" : "Private — click to share with all staff"}
                            data-testid={`toggle-default-${t.id}`}>
                      <Star size={16} weight="fill" className={t.is_default ? "text-[#0F62FE]" : "text-[#F1C21B]"} />
                    </button>
                  ) : (
                    <Star size={16} weight="fill" className={`mt-0.5 shrink-0 ${t.is_default ? "text-[#0F62FE]" : "text-[#F1C21B]"}`} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate flex items-center gap-2">
                      <span className="truncate">{t.title}</span>
                      {titleCounts[(t.title || "").toLowerCase().trim()] > 1 && (
                        <span className="shrink-0 inline-flex items-center text-[9px] font-mono uppercase tracking-widest text-[#DA1E28] bg-[#FFF1F2] border border-[#DA1E28]/40 px-1.5 py-0.5"
                              title="Another template shares this title — the admin editor might be updating the other copy. Click 'Clean up duplicates' in the header.">
                          Dupe
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-0.5">
                      <span className={t.is_default ? "text-[#0F62FE]" : ""}>{badge}</span> · {category === "resolution" ? (TYPE_LABEL[t.type] || "Directors'") : cfg.singular}
                      {t.updated_at && <> · edited {new Date(t.updated_at).toLocaleDateString("en-GB")}</>}
                    </div>
                  </div>
                </div>
              </button>
              {canEdit && (
                <div className="flex items-center pr-2 gap-0.5 opacity-70 group-hover:opacity-100">
                  <Button size="icon" variant="ghost" className="h-8 w-8"
                          onClick={() => openEdit(t)}
                          data-testid={`edit-template-${t.id}`} title="Edit">
                    <PencilSimple size={14} weight="bold" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-[#DA1E28]"
                          onClick={() => remove(t)}
                          data-testid={`delete-template-${t.id}`} title="Delete">
                    <Trash size={14} weight="bold" />
                  </Button>
                </div>
              )}
            </div>
            );
          })}
        </aside>

        <article className="border border-border min-h-[60vh]">
          {!active ? (
            <div className="p-10 text-sm text-muted-foreground">Pick a {cfg.singular.toLowerCase()} on the left, or click <b>New {cfg.singular}</b>.</div>
          ) : (
            <>
              <header className="flex items-start justify-between px-6 py-4 border-b border-border gap-4">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    <span className={active.is_default ? "text-[#0F62FE]" : ""}>{active.is_default ? "Default" : "Custom"}</span> · {category === "resolution" ? (TYPE_KIND[active.type] || "Directors' Resolution") : cfg.singular}
                  </div>
                  <h2 className="text-2xl font-semibold tracking-tight mt-1">{active.title}</h2>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" className="rounded-sm h-9" onClick={() => openEdit(active)}
                          data-testid="edit-template-btn">
                    <PencilSimple size={14} weight="bold" className="mr-1.5" /> Edit
                  </Button>
                  <Button variant="ghost" className="text-[#DA1E28] h-9" onClick={() => remove(active)}
                          data-testid="delete-template-btn">
                    <Trash size={14} weight="bold" className="mr-1.5" /> Delete
                  </Button>
                  <Button variant="outline" className="rounded-sm h-9" onClick={copyBody} data-testid="copy-template-btn">
                    <Copy size={14} weight="bold" className="mr-2" /> Copy body
                  </Button>
                </div>
              </header>
              <pre className="hidden" />
              {isLayoutPreservedHtml(active.body) ? (
                <div className="px-4 py-4 bg-[#F3F4F6] overflow-auto" style={{ maxHeight: "78vh" }}
                     data-testid="template-body"
                     dangerouslySetInnerHTML={{ __html: sanitize(active.body) }} />
              ) : (
                <div className="px-8 py-6 bg-white resolution-preview"
                     data-testid="template-body"
                     dangerouslySetInnerHTML={{ __html: sanitize(toHtml(active.body)) }} />
              )}
              <footer className="px-6 py-3 border-t border-border font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                Placeholders: <span className="text-foreground">&#123;&#123;COMPANY_NAME&#125;&#125;</span> ·
                <span className="text-foreground">&#123;&#123;SSM&#125;&#125;</span> ·
                <span className="text-foreground">&#123;&#123;MEETING_DATE&#125;&#125;</span> ·
                <span className="text-foreground">&#123;&#123;DIRECTORS&#125;&#125;</span> ·
                <span className="text-foreground">&#123;&#123;BUSINESS_ADDRESS&#125;&#125;</span> auto-fill from the client
              </footer>
            </>
          )}
        </article>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-5xl w-[95vw] rounded-sm max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Custom Template" : "New Custom Template"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-[1fr_180px] gap-3">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Title</Label>
                <Input className="mt-1 rounded-sm" value={form.title}
                       onChange={e => setForm({ ...form, title: e.target.value })}
                       placeholder="e.g. Directors' Resolution — Approval of Lease Agreement"
                       data-testid="template-title-input" />
              </div>
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Type</Label>
                <select className="mt-1 h-9 w-full rounded-sm border border-input bg-transparent px-2 text-sm"
                        value={form.type}
                        onChange={e => setForm({ ...form, type: e.target.value })}
                        data-testid="template-type-select">
                  <option value="director">Directors' Resolution</option>
                  <option value="member">Members' Resolution</option>
                  <option value="form">Form</option>
                  <option value="letter">Letter</option>
                </select>
              </div>
            </div>
            {isAdmin && (
              <label className="flex items-center gap-2 text-sm cursor-pointer p-3 border border-[#0F62FE]/30 bg-[#0F62FE]/[0.04] rounded-sm">
                <input type="checkbox" className="accent-[#0F62FE]"
                       checked={!!form.is_default}
                       onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
                       data-testid="template-is-default-checkbox" />
                <div>
                  <div className="text-[#0F62FE] font-medium text-sm">Share as default for all users</div>
                  <div className="text-[11px] text-muted-foreground font-mono uppercase tracking-widest">
                    Every staff account will see and be able to use this template
                  </div>
                </div>
              </label>
            )}
            <div>
              <Label className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Body</Label>
              {isLayoutPreservedHtml(form.body) ? (
                <>
                  <div className="mt-1 border border-border rounded-sm bg-[#F3F4F6] p-3 max-h-[62vh] overflow-auto"
                       data-testid="template-body-image-preview"
                       dangerouslySetInnerHTML={{ __html: sanitize(form.body) }} />
                  <p className="mt-2 font-mono text-[10px] text-muted-foreground">
                    This template's page renders exactly like the source PDF. The image is fixed — rename the title above to organise it in your library.
                  </p>
                </>
              ) : (
                <div className="mt-1" data-testid="template-body-input">
                  <RichTextEditor
                    value={toHtml(form.body)}
                    onChange={(html) => setForm({ ...form, body: html })}
                    placeholder={cfg.preserveLayout ? "Imported content will appear here…" : "CIRCULAR RESOLUTION OF THE BOARD OF DIRECTORS…"}
                    minHeight={560}
                    placeholders={[
                      { token: "{{COMPANY_NAME}}",     label: "Company" },
                      { token: "{{SSM}}",              label: "SSM" },
                      { token: "{{MEETING_DATE}}",     label: "Meeting Date" },
                      { token: "{{DIRECTORS}}",        label: "Directors" },
                      { token: "{{DIRECTOR_1}}",       label: "Director 1" },
                      { token: "{{DIRECTOR_2}}",       label: "Director 2" },
                      { token: "{{SHAREHOLDER_1}}",    label: "Shareholder 1" },
                      { token: "{{SHAREHOLDER_2}}",    label: "Shareholder 2" },
                      { token: "{{SHARES_1}}",         label: "Shares 1" },
                      { token: "{{SHARES_2}}",         label: "Shares 2" },
                      { token: "{{TOTAL_SHARES}}",     label: "Total Shares" },
                      { token: "{{BUSINESS_ADDRESS}}", label: "Business Address" },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-[#0F62FE] hover:bg-[#0043CE]" onClick={save} disabled={saving}
                    data-testid="save-template-btn">
              {saving ? "Saving…" : (editing ? "Update Template" : "Save Template")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
